<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import NavItem from './NavItem.vue';
import { useSidebar } from '@/composables/useSidebar';
import { useAssignmentStore } from '@/stores';
import { useUserStore } from '@/stores/userStore';
import type { NavConfig } from '@/config/navigation';
import { learnerNav } from '@/config/navigation';

const props = withDefaults(
  defineProps<{
    navConfig?: NavConfig;
  }>(),
  {
    navConfig: () => learnerNav,
  }
);

const route = useRoute();
const { sidebarClasses, isMobileOpen, closeMobile } = useSidebar();
const assignmentStore = useAssignmentStore();
const userStore = useUserStore();

const expandedIndex = ref<number | null>(null);

const handleToggle = (index: number) => {
  expandedIndex.value = expandedIndex.value === index ? null : index;
};

// Check if a path is active - exact match only for top-level nav items
const isActive = (path: string) => {
  // Parse the path to separate base path from query params
  const [basePath, queryString] = path.split('?');

  // Exact match only (no child route matching for non-expandable items)
  const pathMatches = route.path === basePath;

  // If path has query params, also check those match
  if (pathMatches && queryString) {
    const params = new URLSearchParams(queryString);
    for (const [key, value] of params.entries()) {
      if (route.query[key] !== value) {
        return false;
      }
    }
  }

  return pathMatches;
};

// An expandable section counts as active when the route matches the parent's
// own `to` (if it has one) OR any of its sub-items. Without the parent check,
// deep-linking to e.g. `/pathways` would leave the section collapsed because
// the parent path is not duplicated as a sub-item.
const isExpandableActive = (item: { to?: string; subItems?: Array<{ to: string; label: string }> }) => {
  if (item.to && isActive(item.to)) return true;
  return item.subItems?.some((sub) => isActive(sub.to)) ?? false;
};

// Find the index of the section that should be expanded based on current route
const findActiveExpandableIndex = (): number | null => {
  const items = props.navConfig.items;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item && item.expandable && isExpandableActive(item)) {
      return i;
    }
  }
  return null;
};

// Auto-expand/collapse based on route changes
watch(
  () => route.path,
  () => {
    const activeIndex = findActiveExpandableIndex();
    // If we're in an expandable section, expand it
    // If we're in a non-expandable item, collapse all
    expandedIndex.value = activeIndex;
  },
  { immediate: true }
);

const navItems = computed(() => {
  return props.navConfig.items.filter((item) => {
    if (item.featureFlag) return userStore.featureFlags[item.featureFlag];
    return true;
  }).map((item) => {
    // Add badge for "My Assignments" nav item
    const updatedItem = item.label === 'My Assignments' && assignmentStore.overdueCount > 0
      ? { ...item, badge: assignmentStore.overdueCount }
      : { ...item };

    // Filter and add active state to sub-items
    if (updatedItem.subItems) {
      updatedItem.subItems = updatedItem.subItems
        .filter((subItem) => !subItem.featureFlag || userStore.featureFlags[subItem.featureFlag])
        .map((subItem) => ({
          ...subItem,
          active: isActive(subItem.to),
        }));
    }

    return updatedItem;
  });
});

const footerItem = computed(() => props.navConfig.footerItem);
</script>

<template>
  <!-- Mobile overlay -->
  <div
    class="sidebar-overlay"
    :class="{ show: isMobileOpen }"
    @click="closeMobile"
  ></div>

  <aside id="sidebar" class="sidebar" :class="sidebarClasses" aria-label="Main navigation">
    <nav class="sidebar-nav" aria-label="Primary">
      <NavItem
        v-for="(item, index) in navItems"
        :key="index"
        :to="item.to"
        :icon="item.icon"
        :label="item.label"
        :badge="item.badge"
        :expandable="item.expandable"
        :sub-items="item.subItems"
        :active="item.expandable ? isExpandableActive(item) : (item.to ? isActive(item.to) : false)"
        :is-expanded="expandedIndex === index"
        @toggle="handleToggle(index)"
      />
    </nav>

    <div v-if="footerItem" class="sidebar-footer">
      <div class="nav-item">
        <!-- External destinations stay as <a target="_blank"> so they open
             in a new tab. Internal destinations route via <RouterLink> so
             clicking doesn't trigger a full page reload. -->
        <a v-if="footerItem.external" :href="footerItem.to" target="_blank" rel="noopener noreferrer">
          <bb-icon :name="footerItem.icon" class="nav-icon" decorative></bb-icon>
          <span class="nav-text">{{ footerItem.label }}</span>
          <bb-icon name="chevron_right" class="nav-chevron" decorative></bb-icon>
        </a>
        <RouterLink v-else :to="footerItem.to">
          <bb-icon :name="footerItem.icon" class="nav-icon" decorative></bb-icon>
          <span class="nav-text">{{ footerItem.label }}</span>
          <bb-icon name="chevron_right" class="nav-chevron" decorative></bb-icon>
        </RouterLink>
      </div>
    </div>
  </aside>
</template>
